import { useState, useCallback } from 'react';

// === Constants ===
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DB_NAME = "QuanPort_NewsCache";
const STORE_NAME = "analysis_results";
const CACHE_DURATION = 86400000/24*3; // 24시간 : 86400000

// === DB Helpers ===
const openDB = () => new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (e) => {
        if (!e.target.result.objectStoreNames.contains(STORE_NAME)) 
            e.target.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = reject;
});

const accessCache = async (key, data = null) => {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, "readwrite"); 
        const store = tx.objectStore(STORE_NAME);
        
        if (data) {
            return new Promise((resolve, reject) => {
                const request = store.put({ content: data, timestamp: Date.now() }, key);
                request.onsuccess = () => resolve(true);
                request.onerror = () => reject(request.error);
            });
        }
        
        return new Promise(resolve => {
            const req = store.get(key);
            req.onsuccess = () => {
                const result = req.result;
                if (result && (Date.now() - result.timestamp < CACHE_DURATION)) {
                    resolve(result.content);
                } else {
                    resolve(null);
                }
            };
            req.onerror = () => resolve(null);
        });
    } catch (e) { console.warn("DB Error", e); return null; }
};

// === Data Processing Logic ===
const calculateSMA = (data, w = 7) => data.map((_, i, arr) => 
    i < w - 1 ? arr[i] : arr.slice(i - w + 1, i + 1).reduce((a, b) => a + b, 0) / w
);

const processData = (rawPrices, rawNews, target, type) => {
    // console.log("Raw News Sample:", rawNews[0]); // 첫 번째 뉴스 데이터 구조 확인
    // 1. 뉴스 정렬 및 슬라이싱
    const sortedNews = [...rawNews].sort((a, b) => new Date(b.date) - new Date(a.date));
    const news = sortedNews.slice(0, 1000);

    // [수정] 가격 데이터 필터링 방어 로직
    let prices = rawPrices;
    if (news.length > 0 && rawPrices.length > 0) {
        const oldestNewsDate = news[news.length - 1].date.split('T')[0];
        const filtered = rawPrices.filter(p => p.date >= oldestNewsDate);
        // 필터링 결과가 너무 적으면 원본 가격 데이터를 사용하여 차트가 끊기지 않게 함
        prices = filtered.length > 2 ? filtered : rawPrices;
    }

    const sortedPriceDates = prices.map(p => p.date).sort();
    const dateMapper = {};
    let priceIdx = 0;
    const allNewsDates = [...new Set(news.map(n => n.date.split('T')[0]))].sort();
    
    allNewsDates.forEach(nDate => {
        while(priceIdx < sortedPriceDates.length && sortedPriceDates[priceIdx] < nDate) {
            priceIdx++;
        }
        if (priceIdx < sortedPriceDates.length) {
            dateMapper[nDate] = sortedPriceDates[priceIdx];
        }
    });

    const sentMap = news.reduce((acc, { date, sentiment }) => {
        const rawDate = date.split('T')[0];
        const mappedDate = dateMapper[rawDate] || rawDate; // 매핑 실패 시 원본 날짜라도 사용
        if (!acc[mappedDate]) acc[mappedDate] = { sum: 0, cnt: 0 };
        acc[mappedDate].sum += (sentiment?.polarity || 0);
        acc[mappedDate].cnt++;
        return acc;
    }, {});

    const dates = [], close = [], sents = [];
    prices.forEach(p => {
        dates.push(p.date);
        close.push(p.close);
        const s = sentMap[p.date];
        sents.push(s ? s.sum / s.cnt : 0); 
    });

    // === 5. Network Data Separation Logic (Updated) ===
    const symbolCount = {};
    const tagCount = {};

    news.forEach(n => {
        // 심볼 빈도 계산
        n.symbols?.forEach(s => {
            const clean = s.split('.')[0];
            // 검색 대상(Target) 자체는 심볼 네트워크에서 제외하여 루프 방지
            if (type === 'ticker' && clean === target.replace('.US','')) return; 
            symbolCount[clean] = (symbolCount[clean] || 0) + 1;
        });
        // 태그 빈도 계산
        n.tags?.forEach(t => {
            // 검색 대상과 동일한 태그는 제외 (선택사항)
            if (t.toLowerCase() === target.toLowerCase()) return;
            tagCount[t] = (tagCount[t] || 0) + 1;
        });
    });

    // 네트워크 생성 헬퍼 함수
    const createNetwork = (counts, centerLabel, isSymbol) => {
        const sortedEntries = Object.entries(counts).sort(([,a], [,b]) => b - a).slice(0, 10);
        // 최대값이 없으면 1로 방어
        const maxVal = sortedEntries.length > 0 ? sortedEntries[0][1] : 1;

        const nodes = sortedEntries.map(([id, val], i, arr) => ({
            id, 
            label: id, 
            type: isSymbol ? 'symbol' : 'tag', // 타입 부여
            // 크기 정규화: 최소 15, 최대 30 가변
            size: 15 + (val / maxVal) * 15, 
            x: Math.cos((i / arr.length) * 2 * Math.PI) * 0.8,
            y: Math.sin((i / arr.length) * 2 * Math.PI) * 0.8
        }));
        
        // 중앙 노드 추가
        nodes.unshift({ 
            id: 'CENTER', 
            label: centerLabel, 
            type: 'center', 
            size: 35, // 중앙은 조금 더 크게
            x: 0, y: 0 
        });

        const edges = nodes.slice(1).map(n => ({ source: 'CENTER', target: n.id }));
        return { nodes, edges };
    };

    // 두 가지 버전의 네트워크 생성
    const centerLabel = type === 'tag' ? `#${target}` : target;
    const networkSymbol = createNetwork(symbolCount, centerLabel, true);
    const networkTag = createNetwork(tagCount, centerLabel, false);

    // 6. Events Logic
    const events = {};
    ["Regulatory", "Historic", "Record", "Warning", "Growth", "Surge", "Drop"].forEach(k => {
        const relNews = news.filter(n => (n.title+n.content).includes(k));
        let up = 0, retSum = 0, count = 0;
        relNews.forEach(n => {
            const mappedDate = dateMapper[n.date.split('T')[0]];
            const idx = prices.findIndex(p => p.date === mappedDate);
            if (idx > -1 && idx + 21 < prices.length) {
                const ret = ((prices[idx + 21].close - prices[idx].close) / prices[idx].close) * 100;
                count++; retSum += ret; if (ret > 0) up++;
            }
        });
        events[k] = count ? { count, winRate: Math.round(up/count*100), avgReturn: (retSum/count).toFixed(2) } : { count: 0 };
    });

    return { 
        dates, prices: close, sentiments: calculateSMA(sents), 
        networkSymbol, // [NEW] 종목 네트워크
        networkTag,    // [NEW] 태그 네트워크
        events, 
        allNews: news, 
        raw: { prices: rawPrices, news: rawNews } 
    };
};

export const useSentimentData = () => {
    const [state, setState] = useState({ isLoading: false, data: null, isCached: false, error: null });

    const loadData = useCallback(async (target, type, forceRefresh = false) => {
        setState(p => ({ ...p, isLoading: true, error: null }));
        
        const currentKey = `${target}_${type}_top1000`;
        
        try {
            if (!forceRefresh) {
                const cachedData = await accessCache(currentKey);
                if (cachedData) {
                    console.log("캐시 데이터 로드 성공:", target);
                    return setState({ 
                        isLoading: false, 
                        data: { ...cachedData, targetName: target }, 
                        isCached: true, 
                        error: null 
                    });
                }
            }
            
            console.log("새 API 요청 발생:", target);
            const res = await fetch(`${API_BASE_URL}/news?target=${target}&type=${type}&period=1Y`);
            
            if (!res.ok) throw new Error(`API Error: ${res.status}`);

            const raw = await res.json();
            if (!raw.prices && !raw.news) throw new Error("No data found");

            const processed = processData(raw.prices || [], raw.news || [], target, type);
            
            await accessCache(currentKey, processed);

            setState({ 
                isLoading: false, 
                data: { ...processed, targetName: target }, 
                isCached: false, 
                error: null 
            });

        } catch (e) {
            console.error(e);
            setState(p => ({ ...p, isLoading: false, error: e.message, data: null }));
        }
    }, []);

    return { ...state, loadData };
};